import { useState } from "react";
import toast from "react-hot-toast";

import FloatingShape from "../components/FloatingShape";
import { parseSemanticVersion } from "../utilities/versionHelper.js";
import { useAuthStore } from "../store/authStore";
import api from "../utilities/api.js";

import { ImportSection } from "./ToolsPage-Import.jsx";
import { ExportSection } from "./ToolsPage-Export.jsx";
import { StandardsImportSection } from "./ToolsPage-StandardsImport.jsx";
import { StandardRefsExportSection } from "./ToolsPage-StandardRefsExport.jsx";
import {
  importFileFormatOptions,
  importOptionOptions,
  getExportFileFormatOptions,
  getExportFilterOptions,
} from "./ToolsPage-options.js";

const toolsTabs = [
  { label: "Export", value: "export" },
  { label: "Import", value: "import" },
];

// Same sidebar styling as the Admin page
const sidebarButtonBase =
  "w-full text-left px-4 py-3 text-lg font-medium rounded-lg transition duration-300";
const sidebarButtonActive = "bg-gray-100 text-gray-800";
const sidebarButtonInactive =
  "text-gray-100 hover:bg-cyan-700 hover:text-white";

const ToolsPage = () => {
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const isMember = useAuthStore((state) => state.isMember);

  // Check if user is admin for showing import section
  const showImportSection = isAdmin();

  // Get role-based export options for dynamic height calculation
  const exportFileFormatOptions = getExportFileFormatOptions(isAdmin, isMember);
  const exportFilterOptions = getExportFilterOptions(isAdmin, isMember);

  // Calculate the maximum number of options across all RadioGroups
  const allOptionCounts = [
    ...(showImportSection
      ? [importFileFormatOptions.length, importOptionOptions.length]
      : []),
    exportFileFormatOptions.length,
    exportFilterOptions.length,
  ];
  const maxOptions = Math.max(...allOptionCounts);

  // Calculate dynamic height: base height + (option height * number of options) + padding + bottom margin
  const baseHeight = 60;
  const optionHeight = 28;
  const bottomMargin = 20;
  const dynamicHeight = baseHeight + maxOptions * optionHeight + bottomMargin;

  // State variables for import/export options and file handling
  const [activeTab, setActiveTab] = useState("export");
  const [exportFileFormat, setExportFileFormat] = useState("json");
  const [exportFilter, setExportFilter] = useState("public");
  const [importFileFormat, setImportFileFormat] = useState("json");
  const [importOption, setImportOption] = useState("skipExisting");
  const [importFile, setImportFile] = useState(null);
  const [exportVersionInput, setExportVersionInput] = useState("");
  const [exportVersion, setExportVersion] = useState("");
  const [wrongInput, setWrongInput] = useState(false);

  // --- DATA IMPORT ---

  const handleFileChange = (event) => {
    setImportFile(event.target.files[0]);
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import.", {
        duration: 5000,
        position: "top-center",
      });
      return;
    }

    const extension = importFile.name.split(".").pop().toLowerCase();
    if (
      (importFileFormat === "json" && extension !== "json") ||
      (importFileFormat === "csv" && extension !== "csv") ||
      (importFileFormat === "xlsx" && extension !== "xlsx")
    ) {
      toast.error(
        `Selected file type (.${extension}) does not match the chosen format (${importFileFormat.toUpperCase()}).`,
        {
          duration: 4000,
          position: "top-center",
          className: "whitespace-pre-line",
        },
      );
      return;
    }

    let url = `/import/import-json`;
    if (importFileFormat === "csv") {
      url = `/import/import-csv`;
    } else if (importFileFormat === "xlsx") {
      url = `/import/import-xlsx`;
    }

    const formData = new FormData();
    formData.append("file", importFile);
    formData.append("format", importFileFormat);
    formData.append("importOption", importOption);

    try {
      const response = await api.post(url, formData);

      if (response.status === 200) {
        toast.success("Import successful!\n" + " " + response.data.message, {
          duration: 4000,
          position: "top-center",
          className: "whitespace-pre-line",
        });
      }
    } catch (error) {
      console.error("Error importing file:", error);
      const backendMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Unknown error";
      toast.error("Import failed!\n" + backendMsg, {
        duration: 5000,
        position: "top-center",
        className: "whitespace-pre-line",
      });
    }
  };

  // --- DATA EXPORT ---

  const handleExportVersionChange = (e) => {
    const value = e.target.value;
    setExportVersionInput(value);

    if (value.trim() !== "") {
      const parsed = parseSemanticVersion(value);
      setWrongInput(!parsed);
      if (parsed) {
        setExportVersion(parsed);
      } else {
        setExportVersion("");
      }
    } else {
      setWrongInput(false);
      setExportVersion("");
    }
  };

  const handleExportVersionBlur = (e) => {
    const value = e.target.value;
    if (value.trim() !== "") {
      const parsed = parseSemanticVersion(value);
      setWrongInput(!parsed);
      if (parsed) {
        setExportVersion(parsed);
      }
    } else {
      setWrongInput(false);
      setExportVersion("");
    }
  };

  const handleExport = async () => {
    let url = `/export/exportFile`;

    try {
      const response = await api.post(
        url,
        {
          format: exportFileFormat,
          filterOption: exportFilter,
          filterVersion: exportVersion ? exportVersion : null,
        },
        {
          responseType: "blob",
        },
      );

      let filename = "exported_file." + exportFileFormat;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("filename=") !== -1) {
        filename = disposition
          .split("filename=")[1]
          .replace(/['"]/g, "")
          .trim();
      }

      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = urlBlob;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlBlob);

      const exportedCount = response.headers["x-exported-count"];
      toast.success(
        "Export successful!\n" + " " + exportedCount + " documents exported.",
        {
          duration: 4000,
          position: "top-center",
        },
      );
    } catch (error) {
      console.error("Error exporting file:", error);

      let errorMessage = "Export failed!";
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorMessage;
          } catch {
            errorMessage =
              error.response?.data?.message || error.message || errorMessage;
          }
        } else {
          errorMessage =
            error.response.data.message || error.message || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        duration: 5000,
        position: "top-center",
      });
    }
  };

  // Admins switch between Export and Import in a sidebar (like the Admin page)
  const showExport = !showImportSection || activeTab === "export";
  const showImport = showImportSection && activeTab === "import";

  return (
    <div className="min-h-[800px] h-full border border-t-0 border-gray-100 bg-gradient-to-br from-cyan-900 via-cyan-800 to-cyan-900 flex flex-col md:flex-row relative overflow-hidden">
      <FloatingShape
        color="bg-cyan-500"
        size="w-64 h-64"
        top="0%"
        left="20%"
        delay={0}
      />
      <FloatingShape
        color="bg-cyan-500"
        size="w-48 h-48"
        top="70%"
        left="80%"
        delay={5}
      />
      <FloatingShape
        color="bg-cyan-500"
        size="w-32 h-32"
        top="40%"
        left="10%"
        delay={2}
      />

      {/* Sidebar (admin only) — horizontal on mobile, vertical on md+ */}
      {showImportSection && (
        <div className="bg-gradient-to-b from-cyan-900 to-cyan-800 md:w-48 flex-shrink-0 relative z-10">
          {/* Mobile: horizontal tabs */}
          <div className="flex md:hidden p-2 gap-2">
            {toolsTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
                  activeTab === tab.value
                    ? sidebarButtonActive
                    : sidebarButtonInactive
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Desktop: vertical sidebar */}
          <div className="hidden md:flex flex-col gap-2 p-3">
            {toolsTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`${sidebarButtonBase} ${
                  activeTab === tab.value
                    ? sidebarButtonActive
                    : sidebarButtonInactive
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 ml-0 lg:ml-10 mt-12 text-white relative z-10">
        <h1 className="ml-4 text-3xl text-white font-bold mb-5">
          {showImport ? "Data Import" : "Data Export"}
        </h1>

        {/* On the Export tab, boxes side by side get the same height, so their
            buttons (mt-auto) line up */}
        <div
          className={`flex flex-col text-sm lg:text-base lg:flex-row lg:flex-wrap items-start mb-8 gap-8 lg:min-h-[32rem] ${
            showExport ? "lg:items-stretch" : ""
          }`}
        >
          {/* Import Options - Admin only */}
          {showImport && (
            <ImportSection
              dynamicHeight={dynamicHeight}
              importFileFormat={importFileFormat}
              setImportFileFormat={setImportFileFormat}
              importOption={importOption}
              setImportOption={setImportOption}
              importFile={importFile}
              handleFileChange={handleFileChange}
              handleImport={handleImport}
            />
          )}

          {/* Import Standards - Admin only */}
          {showImport && (
            <StandardsImportSection dynamicHeight={dynamicHeight} />
          )}

          {/* Export Options - All authenticated users */}
          {showExport && (
            <ExportSection
              dynamicHeight={dynamicHeight}
              exportFileFormat={exportFileFormat}
              setExportFileFormat={setExportFileFormat}
              exportFilter={exportFilter}
              setExportFilter={setExportFilter}
              exportVersionInput={exportVersionInput}
              handleExportVersionChange={handleExportVersionChange}
              handleExportVersionBlur={handleExportVersionBlur}
              wrongInput={wrongInput}
              handleExport={handleExport}
            />
          )}

          {/* Export Standard References - Admin only */}
          {showExport && showImportSection && (
            <StandardRefsExportSection dynamicHeight={dynamicHeight} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolsPage;
